import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Redirect, useLocation, useParams} from 'react-router-dom';
import {Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {canOpenReportBuilder} from './reportBuilderGate';
import {BuilderRole, ReportBuilderProvider, ReportIntentPayload} from './ReportBuilderContext';
import {ReportBuilderSurface} from './ReportBuilderSurface';
import {validateReportDefinition} from './contracts/validator';
import {LoadedReport, ReportDataSource} from './data/ReportDataSource';
import {HttpReportDataSource} from './data/HttpReportDataSource';
import type {ReportTaxonomy} from './contracts/report-taxonomy.types';
import {useReportTaxonomy} from './useReportTaxonomy';
import {SavedReportRef} from './data/savedReportInput';

/** Production data source: the real `POST /report/data/fetch` transport. A module singleton so a
 * re-render does not churn a new instance (keeps the provider's memoised source stable).
 * Tests/Storybook inject a `MockReportDataSource` via the `dataSource` prop instead. */
const httpReportDataSource: ReportDataSource = new HttpReportDataSource();

/** Report intent handed to the page through react-router location state when the chat card's "View"
 * navigates here. The intent is ephemeral (agent-generated, not persisted), so a refresh or direct
 * hit carries no state and the page redirects away. */
interface ReportBuilderLocationState {
	initialConfig?: ReportIntentPayload;
	/** Logged-in person id, scopes the per-user layout overlay. Falls back to the provider's 'me'. */
	userId?: string;
	/** Task-report taxonomy fetched by the chat surface when the intent arrived. Optional — a
	 * failed/slow fetch leaves this undefined and the provider falls back to its own null default,
	 * which disables condition editing until a taxonomy fetch elsewhere supplies one. */
	taxonomy?: ReportTaxonomy;
}

/** Fills the routed content area as a normal page (in-flow, not a portalled full-viewport takeover).
 * The surface itself scrolls. */
const Surface = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: auto;
`;

const ErrorPanel = styled.div`
	padding: 24px;
`;

export interface ReportBuilderPageProps {
	/** Overridable for tests/Storybook; production defaults to the live `HttpReportDataSource`. */
	dataSource?: ReportDataSource;
}

interface ReportBuilderViewProps {
	initialConfig: ReportIntentPayload;
	userId?: string;
	/** Taxonomy the chat surface may have passed in router state; used only to seed the fetch so
	 * editing works instantly when present. The view fetches its own regardless. */
	taxonomySeed?: ReportTaxonomy;
	dataSource: ReportDataSource;
	/** The Reports-list entry this report already exists as, when opened on a saved report rather
	 * than a fresh draft. */
	savedReportSeed?: SavedReportRef;
	/** Defaults to editor: the only way to reach the builder without a stored report is the chat
	 * intent, and the person who just generated a draft is its author. */
	role?: BuilderRole;
}

/** Mounts the provider + surface once the page guards have passed. Kept as its own component so the
 * taxonomy fetch hook runs unconditionally (never behind the page's early returns), and so the
 * builder resolves its own taxonomy rather than depending on the ephemeral chat handoff — a
 * missing/slow/failed handoff no longer hides the conditions editor. */
function ReportBuilderView({
	initialConfig,
	userId,
	taxonomySeed,
	dataSource,
	savedReportSeed,
	role = 'editor',
}: ReportBuilderViewProps): JSX.Element {
	const taxonomy = useReportTaxonomy(taxonomySeed);
	return (
		// Key by draftId so a refined create_report_intent (new draftId) remounts the provider with
		// the new definition. The provider seeds definition/grouping/tableState once on mount with no
		// reset-on-payload, so without a changing key a refinement would keep the previous draft.
		// Falls back to a stable key for non-intent opens.
		<ReportBuilderProvider
			key={initialConfig.draftId ?? 'report-builder'}
			payload={initialConfig}
			role={role}
			dataSource={dataSource}
			userId={userId}
			taxonomy={taxonomy}
			savedReportSeed={savedReportSeed}
		>
			<ReportBuilderSurface />
		</ReportBuilderProvider>
	);
}

type LoadState = {status: 'loading'} | {status: 'loaded'; report: LoadedReport} | {status: 'failed'};

/** Re-opens a report that was saved from the builder, loading its definition by id. The report intent
 * the chat hands over is ephemeral, so a saved report has no router state to restore from and its
 * definition is read back from storage instead. */
function SavedReportBuilder({savedReportId, dataSource}: {savedReportId: string; dataSource: ReportDataSource}): JSX.Element {
	const {formatMessage} = useIntl();
	const [state, setState] = useState<LoadState>({status: 'loading'});

	useEffect(() => {
		let cancelled = false;
		setState({status: 'loading'});
		void dataSource.loadDefinition(savedReportId).then(
			stored => {
				if (cancelled) {
					return;
				}
				setState({status: 'loaded', report: stored});
			},
			() => {
				if (!cancelled) {
					setState({status: 'failed'});
				}
			}
		);
		return () => {
			cancelled = true;
		};
	}, [savedReportId, dataSource]);

	if (state.status === 'loading') {
		return <ErrorPanel data-cy="ai-report-builder-loading" />;
	}

	if (state.status === 'failed') {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_load_failed'})}</Text>
			</ErrorPanel>
		);
	}

	const {valid, errors} = validateReportDefinition(state.report.definition);
	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	// Anything short of edit rights is a reader. Failing closed matters more than being generous:
	// showing edit affordances the backend will refuse is worse than hiding ones it would allow.
	const role: BuilderRole =
		state.report.viewerPermission === 'OWNER' || state.report.viewerPermission === 'EDITOR' ? 'editor' : 'reader';

	// Keyed by report id so navigating between two saved reports remounts the provider, which seeds its
	// working state once on mount.
	return (
		<ReportBuilderView
			key={savedReportId}
			initialConfig={{
				definition: state.report.definition,
				draftId: savedReportId,
				prompt: state.report.promptText ?? undefined,
				source: state.report.source,
			}}
			dataSource={dataSource}
			savedReportSeed={{id: savedReportId, privateAccess: state.report.privateAccess}}
			role={role}
		/>
	);
}

/** The AI report builder rendered as a routed view (path `/report-builder`), mounted in the app's
 * main content region beside the primary navigation. Reached only from the chat card's "View", which
 * navigates here with the report intent in location state. Security-critical gate: redirects to `/`
 * unless `canOpenReportBuilder()` passes AND an intent is present in location state. Guards a
 * malformed `initialConfig.definition` (e.g. an upstream agent response that fails schema
 * validation) by rendering an error block instead of crashing the surface/provider. */
export function ReportBuilderPage({dataSource = httpReportDataSource}: ReportBuilderPageProps): JSX.Element {
	const {formatMessage} = useIntl();
	const location = useLocation<ReportBuilderLocationState>();
	const {reportId} = useParams<{reportId?: string}>();
	const state = location.state;

	if (!canOpenReportBuilder()) {
		return <Redirect to="/" />;
	}

	// Re-opening a saved report: the definition comes from storage, not from router state.
	if (reportId) {
		return (
			<Surface data-cy="ai-report-builder-page">
				<SavedReportBuilder savedReportId={reportId} dataSource={dataSource} />
			</Surface>
		);
	}

	if (!state || !state.initialConfig) {
		return <Redirect to="/" />;
	}

	const {initialConfig, userId, taxonomy} = state;
	const {valid, errors} = validateReportDefinition(initialConfig.definition);

	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	return (
		<Surface data-cy="ai-report-builder-page">
			<ReportBuilderView initialConfig={initialConfig} userId={userId} taxonomySeed={taxonomy} dataSource={dataSource} />
		</Surface>
	);
}

export default ReportBuilderPage;
